import { FC, useCallback, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AnyAction } from 'redux';
import { ThunkDispatch } from 'redux-thunk';
import { useRouter } from 'shared-routing';
import { ForgottenPasswordTemplate, ForgottenPasswordTemplateLabels } from 'voyager';

import { Routes } from '@common/constants';
import { useTranslations } from '@lang';
import { entitySelectors } from '@store/entity/selectors';
import { resetGuestPassword } from '@store/guest/actions';
import { EMAIL_REGEXP } from '@utils/validators';

import { ForgottenPasswordV2Props } from './ForgottenPasswordV2.types';

type AppDispatch = ThunkDispatch<unknown, unknown, AnyAction>;

export const ForgottenPasswordV2: FC<ForgottenPasswordV2Props> = ({ property }) => {
  const { t } = useTranslations();
  const router = useRouter();
  const dispatch = useDispatch<AppDispatch>();

  const homepage = useSelector((state) => {
    const propertyHomepage = entitySelectors.getProperty(state, property.id)?.homepage;
    return propertyHomepage ? entitySelectors.getHomepage(state, propertyHomepage.id) : null;
  });

  const [email, setEmail] = useState('');
  const [error, setError] = useState<string | undefined>(undefined);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const handleEmailChange = useCallback((value: string) => {
    setEmail(value);
    setError(undefined);
  }, []);

  const handleSubmit = useCallback(async () => {
    const trimmedEmail = email.trim();
    if (!trimmedEmail) {
      setError(t('error.required'));
      return;
    }
    if (!EMAIL_REGEXP.test(trimmedEmail)) {
      setError(t('error.email'));
      return;
    }

    setIsSubmitting(true);
    setError(undefined);
    try {
      await dispatch(resetGuestPassword(property.id, { email }));
      setIsSuccess(true);
    } catch (e) {
      setError(t('error.resetPasswordFailed'));
    } finally {
      setIsSubmitting(false);
    }
  }, [dispatch, property.id, email, t]);

  const handleBackToSignIn = useCallback(() => {
    router.push({ pathname: Routes.SIGNIN });
  }, [router]);

  const labels: ForgottenPasswordTemplateLabels = useMemo(
    () => ({
      title: t('forgottenPassword.header'),
      subtitle: t('forgottenPassword.description'),
      emailLabel: t('forgottenPassword.emailLabel'),
      submit: t('forgottenPassword.send'),
      backToSignIn: t('forgottenPassword.backToLogin'),
    }),
    [t]
  );

  return (
    <ForgottenPasswordTemplate
      propertyName={property.name}
      propertyLogoUrl={property.logo?.url}
      heroImageUrl={homepage?.heroBackgroundImage?.url}
      email={email}
      onEmailChange={handleEmailChange}
      onSubmit={handleSubmit}
      isSubmitting={isSubmitting}
      error={error}
      onBackToSignIn={handleBackToSignIn}
      successMessage={isSuccess ? t('forgottenPassword.successMessage') : undefined}
      labels={labels}
    />
  );
};
